import { useState, useEffect, useCallback } from 'react';
import { Button, Drawer, Checkbox, message } from 'antd';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import InputItem from './InputItem';

import request from '@/request';

export default function ScoreEditor({
  item,
  open,
  contestType,
  onCancel,
  onSubmit,
  groupName,
  itemsGroup,
}) {
  const [loading, setLoading] = useState(false);
  const [scoreItems, setScoreItems] = useState([]);
  const [formData, setFormData] = useState(
    item || {
      attr: {},
      options: [],
    }
  );
  const [c1Disabked, setC1Disabked] = useState(!!formData.attr?.tolerate);
  const [c2Disabked, setC2Disabked] = useState(!!formData.attr?.maxTolerate);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    request.get('/events/admin/score/item/types').then((res) => {
      const allItems = itemsGroup
        .map((i) => i.items)
        .reduce((l, n) => l.concat(n), []);
      if (contestType === 1) {
        let newList = res;
        newList = newList.filter((i) => i.id !== 10);
        if (groupName.indexOf('得分') !== -1) {
          newList = res.filter((i) => i.ifBonus === 1);
          const idNineExist = allItems.find((i) => i.scoreTypeId === 9);
          if (idNineExist) newList = newList.filter((i) => i.id !== 9);
        } else if (groupName.indexOf('扣分') !== -1) {
          const allItems = itemsGroup
            .map((i) => i.items)
            .reduce((l, n) => l.concat(n), []);
          newList = res.filter((i) => i.ifDeduction === 1);
          const idSixExist = allItems.find((i) => i.scoreTypeId === 6);
          const idSevenExist = allItems.find((i) => i.scoreTypeId === 7);
          if (idSixExist) newList = newList.filter((i) => i.id !== 6);
          if (idSevenExist) newList = newList.filter((i) => i.id !== 7);
        }
        setScoreItems(newList);
      } else if (contestType === 2) {
        const id10Exist = allItems.find((i) => i.scoreTypeId === 10);
        const id11Exist = allItems.find((i) => i.scoreTypeId === 11);
        let newList = [];
        /*
         * 修复：res.find() 若没有匹配项会返回 undefined，
         * 直接 push undefined 会导致渲染时 s.id 报错。
         * 先用变量承接 find 结果，仅在结果非空时才加入列表。
         */
        const item10 = res.find((i) => i.id === 10);
        const item11 = res.find((i) => i.id === 11);
        if (!id10Exist && item10) newList.push(item10);
        if (!id11Exist && item11) newList.push(item11);
        setScoreItems(newList);
      }
    });
  }, [contestType, groupName, itemsGroup]);

  useEffect(() => {
    if (formData?.options?.length !== 0) return;
    if (formData.scoreTypeId === 1 || formData.scoreTypeId === 8) {
      updateField('options', [{ score: 0 }, { score: 0 }]);
    }
  }, [formData]);

  // 保存前校验计分项必填字段及字段格式
  const onClick = useCallback(async () => {
    if (!formData.scoreTypeId) {
      message.warning('请选择类型');
      return;
    } else if (!formData.name) {
      message.warning('请输入描述');
      return;
    } else if (!formData.options?.length === 0) {
      message.warning('请输入分值');
      return;
    } else if (
      formData.scoreTypeId === 4 &&
      (formData.attr?.per === undefined || formData.attr?.per === null)
    ) {
      message.warning('请输入每次扣除秒数');
      return;
    } else if (
      formData.scoreTypeId === 4 &&
      (!Number.isInteger(formData.attr.per) || formData.attr.per < 0)
    ) {
      message.warning('每次扣除秒数必须为大于等于0的整数');
      return;
    } else if (
      formData.scoreTypeId === 4 &&
      formData.attr?.max !== undefined &&
      formData.attr?.max !== null &&
      (!Number.isInteger(formData.attr.max) || formData.attr.max <= 0)
    ) {
      message.warning('最多扣次数必须为大于0的整数');
      return;
    }
    setLoading(true);
    await onSubmit(formData);
    setLoading(false);
    onCancel();
  }, [formData, onCancel, onSubmit]);

  const updateScoreValue = (value, index) => {
    updateField(
      'options',
      formData.options.map((v, i) => (i == index ? { score: value } : v))
    );
  };

  const removeScoreValue = (index) => {
    updateField(
      'options',
      formData.options.filter((_, i) => i !== index)
    );
  };

  const addScoreValue = () => {
    updateField('options', [...formData.options, { score: 0 }]);
  };

  const updateAttr = (key, value) => {
    updateField('attr', { ...formData.attr, [key]: value });
  };

  return (
    <Drawer
      title="编辑计分项"
      size={600}
      open={open}
      onClose={onCancel}
      extra={
        <Button key="submit" type="primary" loading={loading} onClick={onClick}>
          保存
        </Button>
      }
    >
      <div className="flex flex-col">
        <h2>
          计分项 <span className="text-red-500 mr-1">*</span>
        </h2>
        <div className="flex flex-wrap gap-2 mt-1">
          {scoreItems.map((s) => (
            <Button
              key={s.id}
              onClick={() => updateField('scoreTypeId', s.id)}
              type={s.id === formData.scoreTypeId ? 'primary' : ''}
            >
              {s.name}
            </Button>
          ))}
        </div>
      </div>
      <InputItem
        label="描述"
        require
        value={formData.name}
        onChange={(v) => updateField('name', v)}
        className="my-10"
      />
      {formData.scoreTypeId === 1 && (
        <div className="mt-2 flex gap-2 items-center">
          <span className="w-12 text-sm text-slate-700">得分值</span>
          {formData.options.map((o, index) => (
            <div key={index} className="relative">
              <InputItem
                value={o.score}
                onChange={(v) => updateScoreValue(v, index)}
                disabled={index === formData.options.length - 1}
                className="w-20"
                type="number"
              />
              {index !== 0 && index !== formData.options.length - 1 && (
                <button
                  onClick={() => removeScoreValue(index)}
                  className="absolute -top-1 -right-1 w-4 h-4 text-stone-400 hover:text-stone-400 flex items-center justify-center bg-white rounded-full border border-stone-200 hover:bg-stone-50 cursor-pointer"
                  title="删除"
                >
                  <MinusOutlined className="text-xs" />
                </button>
              )}
            </div>
          ))}
          <button
            onClick={addScoreValue}
            className="w-20 h-10 mt-1 px-2 py-0.5 text-xs bg-white border border-slate-200 rounded-md hover:bg-blue-50"
          >
            <PlusOutlined className="text-lg" />
          </button>
        </div>
      )}
      {formData.scoreTypeId === 2 && (
        <div className="flex items-center gap-2">
          <span>最低得分</span>
          <InputItem
            value={formData.attr?.min}
            onChange={(v) => updateAttr('min', v)}
            className="w-20"
            type="number"
          />
          <span>最高得分</span>
          <InputItem
            value={formData.attr?.max}
            onChange={(v) => updateAttr('max', v)}
            className="w-20"
            type="number"
          />
        </div>
      )}
      {formData.scoreTypeId === 3 && (
        <div className="flex items-center gap-2">
          <span>每次得分</span>
          <InputItem
            value={formData.attr.per}
            onChange={(v) => updateAttr('per', v)}
            className="w-20"
            type="number"
          />
          <span>最高次数</span>
          <InputItem
            value={formData.attr.max}
            onChange={(v) => updateAttr('max', v)}
            className="w-20"
            type="number"
          />
        </div>
      )}
      {formData.scoreTypeId === 4 && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span>
              <span className="text-red-500 mr-1">*</span>
              每次加
            </span>
            <InputItem
              value={formData.attr.per}
              onChange={(v) => updateAttr('per', v)}
              className="w-25"
              type="number"
              min={0}
              precision={0}
              suffix="秒"
            />
            <span>最多加</span>
            <InputItem
              suffix="次"
              value={formData.attr.max}
              onChange={(v) => updateAttr('max', v)}
              className="w-25"
              type="number"
              min={1}
              precision={0}
            />
          </div>
          {/* <div className="flex items-center gap-2">
            <Checkbox
              checked={c1Disabked}
              onChange={(e) => {
                setC1Disabked(e.target.checked);
                if (!e.target.checked) {
                  updateAttr('tolerate', '');
                }
              }}
            >
              犯规
            </Checkbox>
            <InputItem
              suffix="次"
              disabled={!c1Disabked}
              value={formData.attr?.tolerate}
              onChange={(v) => updateAttr('tolerate', v)}
              className="w-25"
              type="number"
            />
            <span>不扣时间</span>
            <Checkbox
              checked={c2Disabked}
              onChange={(e) => {
                setC2Disabked(e.target.checked);
                if (!e.target.checked) {
                  updateAttr('maxTolerate', '');
                }
              }}
            >
              犯规
            </Checkbox>
            <InputItem
              suffix="次"
              disabled={!c2Disabked}
              value={formData.attr?.maxTolerate}
              onChange={(v) => updateAttr('maxTolerate', v)}
              className="w-25"
              type="number"
            />
            <span>时间清零</span>
          </div> */}
        </div>
      )}
      {formData.scoreTypeId === 5 && (
        <div className="flex items-center gap-2">
          <span>最低扣分</span>
          <InputItem
            value={formData.attr?.min}
            onChange={(v) => updateAttr('min', v)}
            className="w-25"
            type="number"
          />
          <span>最高扣分</span>
          <InputItem
            value={formData.attr?.max}
            onChange={(v) => updateAttr('max', v)}
            className="w-25"
            type="number"
          />
        </div>
      )}
      {formData.scoreTypeId === 8 && (
        <div className="mt-2 flex gap-2 items-center">
          <span className="w-12 text-sm text-slate-700">扣分值</span>
          {formData.options.map((o, index) => (
            <div key={index} className="relative">
              <InputItem
                value={o.score}
                onChange={(v) => updateScoreValue(v, index)}
                disabled={index === formData.options.length - 1}
                className="w-20"
                type="number"
              />
              {index !== 0 && index !== formData.options.length - 1 && (
                <button
                  onClick={() => removeScoreValue(index)}
                  className="absolute -top-1 -right-1 w-4 h-4 text-stone-400 hover:text-stone-400 flex items-center justify-center bg-white rounded-full border border-stone-200 hover:bg-stone-50 cursor-pointer"
                  title="删除"
                >
                  <MinusOutlined className="text-xs" />
                </button>
              )}
            </div>
          ))}
          <button
            onClick={addScoreValue}
            className="w-20 h-10 mt-1 px-2 py-0.5 text-xs bg-white border border-slate-200 rounded-md hover:bg-blue-50"
          >
            <PlusOutlined className="text-lg" />
          </button>
        </div>
      )}
      {formData.scoreTypeId === 9 && <div>规则：得分 = 飞行用时</div>}
      {(formData.scoreTypeId === 10 || formData.scoreTypeId === 11) && (
        <div className="flex flex-col gap-4">
          <span>规则：每次惩罚</span>
          <div className="flex items-center gap-4">
            <span>对手加</span>
            <InputItem
              value={formData.attr.them}
              onChange={(value) => updateAttr('them', value)}
              type="number"
              className="w-40"
              suffix="分"
            />
          </div>
          <div className="flex items-center gap-4">
            <span>我方减</span>
            <InputItem
              value={formData.attr.we}
              onChange={(value) => updateAttr('we', value)}
              type="number"
              className="w-40"
              suffix="分"
            />
          </div>
        </div>
      )}
    </Drawer>
  );
}
